import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { getSession, completeSession, addService } from '@/lib/api'
import { formatDateTime } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import {
  ArrowLeft,
  Users,
  Clock,
  MapPin,
  Phone,
  DollarSign,
  AlertCircle,
  Check,
  Plus,
  Building2,
  User,
  Calendar,
  CreditCard,
  Package,
  Shirt,
  UserPlus,
} from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'

interface SessionDetailData {
  id: string
  soft_room_id: string | null
  soft_room: {
    id: string
    name: string
    airport_code: string | null
    terminal: string | null
    phone: string | null
  } | null
  client_id: string | null
  responder_user_id: string | null
  responder_name: string
  responder_agency: string | null
  responder_badge_id: string | null
  case_number: string | null
  victim_first_name: string | null
  victim_last_name: string | null
  victim_phone: string | null
  victim_dob: string | null
  victim_nationality: string | null
  victim_gender: string | null
  is_minor: boolean | null
  needs_medical: boolean
  needs_interpreter: boolean
  interpreter_language: string | null
  intake_notes: string | null
  session_notes: string | null
  status: string
  started_at: string | null
  completed_at: string | null
  duration_minutes: number | null
  services: Array<{
    id: string
    service_type: string
    details: Record<string, unknown>
    monetary_value: number
    quantity: number
    notes: string | null
    provided_at: string | null
  }>
  total_service_value: number
}

const serviceTypeLabels: Record<string, string> = {
  gift_card: 'Gift Card',
  food_voucher: 'Food Voucher',
  backpack: 'Backpack',
  clothing: 'Clothing',
  hygiene_kit: 'Hygiene Kit',
  transportation: 'Transportation',
  bus_pass: 'Bus Pass',
  phone_call: 'Phone Call',
  medical_referral: 'Medical Referral',
  legal_referral: 'Legal Referral',
  shelter_referral: 'Shelter Referral',
  other: 'Other',
}

const quickServiceButtons = [
  { type: 'gift_card', label: 'Gift Card', icon: CreditCard },
  { type: 'food_voucher', label: 'Food Voucher', icon: DollarSign },
  { type: 'backpack', label: 'Backpack', icon: Package },
  { type: 'clothing', label: 'Clothing', icon: Shirt },
  { type: 'hygiene_kit', label: 'Hygiene Kit', icon: Package },
  { type: 'transportation', label: 'Transport', icon: MapPin },
  { type: 'medical_referral', label: 'Medical Ref.', icon: UserPlus },
  { type: 'legal_referral', label: 'Legal Ref.', icon: UserPlus },
  { type: 'shelter_referral', label: 'Shelter Ref.', icon: UserPlus },
  { type: 'other', label: 'Other', icon: Plus },
]

const clothingTypes = [
  'T-Shirt', 'Long Sleeve Shirt', 'Pants/Jeans', 'Shorts', 'Dress',
  'Jacket/Coat', 'Sweater/Hoodie', 'Underwear', 'Socks', 'Shoes',
]

const clothingSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'Child S', 'Child M', 'Child L']

const backpackContents = ['Toiletries', 'Snacks', 'Water Bottle', 'Phone Charger', 'Journal', 'Blanket', 'First Aid']

export default function SessionDetail() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [showAddService, setShowAddService] = useState(false)
  const [selectedServiceType, setSelectedServiceType] = useState<string>('')
  const [serviceValue, setServiceValue] = useState('')
  const [serviceQty, setServiceQty] = useState('1')
  const [serviceNotes, setServiceNotes] = useState('')
  // Rich detail state
  const [giftCardType, setGiftCardType] = useState('Visa')
  const [foodVendor, setFoodVendor] = useState('Airport Restaurant')
  const [bpContents, setBpContents] = useState<string[]>([])
  const [clothingItems, setClothingItems] = useState([{ type: '', size: '', quantity: '1' }])

  const { data: session, isLoading, error } = useQuery<SessionDetailData>({
    queryKey: ['session', sessionId],
    queryFn: () => getSession(sessionId!),
    enabled: !!sessionId,
  })

  const completeMutation = useMutation({
    mutationFn: (notes?: string) => completeSession(sessionId!, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session', sessionId] })
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
      toast({ title: 'Session completed' })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to complete session',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      })
    },
  })

  const addServiceMutation = useMutation({
    mutationFn: addService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session', sessionId] })
      resetServiceForm()
      setShowAddService(false)
      toast({ title: 'Service added' })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to add service',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      })
    },
  })

  const resetServiceForm = () => {
    setSelectedServiceType('')
    setServiceValue('')
    setServiceQty('1')
    setServiceNotes('')
    setGiftCardType('Visa')
    setFoodVendor('Airport Restaurant')
    setBpContents([])
    setClothingItems([{ type: '', size: '', quantity: '1' }])
  }

  const handleAddService = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedServiceType) return

    // Build details based on service type
    const details: Record<string, unknown> = {}
    if (selectedServiceType === 'gift_card') {
      details.card_type = giftCardType
    } else if (selectedServiceType === 'food_voucher') {
      details.vendor = foodVendor
    } else if (selectedServiceType === 'backpack') {
      details.contents = bpContents
    } else if (selectedServiceType === 'clothing') {
      details.items = clothingItems.filter(i => i.type)
    }

    addServiceMutation.mutate({
      session_id: sessionId!,
      service_type: selectedServiceType,
      monetary_value: parseFloat(serviceValue) || 0,
      quantity: parseInt(serviceQty) || 1,
      notes: serviceNotes || undefined,
      details,
    })
  }

  const formatDuration = (minutes: number | null): string => {
    if (minutes === null) return 'In progress'
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error || !session) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Card>
          <CardContent className="p-8 text-center text-destructive">
            <AlertCircle className="h-12 w-12 mx-auto mb-4" />
            <p>Session not found or failed to load</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/sessions')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Sessions
          </Button>
          <h1 className="text-2xl font-bold text-foreground">
            Session Details
            {session.case_number && (
              <span className="text-muted-foreground ml-2">#{session.case_number}</span>
            )}
          </h1>
        </div>
        <span
          className={`px-3 py-1 text-sm rounded-full ${
            session.status === 'completed'
              ? 'sanctuary-badge sanctuary-badge-completed'
              : 'sanctuary-badge sanctuary-badge-active'
          }`}
        >
          {session.status === 'in_progress' ? 'Active' : session.status}
        </span>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <User className="h-8 w-8 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Responder</p>
                <p className="font-semibold">{session.responder_name}</p>
                {session.responder_agency && (
                  <p className="text-xs text-muted-foreground">{session.responder_agency}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-8 w-8 text-chart-3" />
              <div>
                <p className="text-sm text-muted-foreground">Started At</p>
                <p className="font-semibold">
                  {session.started_at ? formatDateTime(session.started_at) : 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-chart-4" />
              <div>
                <p className="text-sm text-muted-foreground">Duration</p>
                <p className="font-semibold">{formatDuration(session.duration_minutes)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="h-8 w-8 text-chart-5" />
              <div>
                <p className="text-sm text-muted-foreground">Services Value</p>
                <p className="font-semibold">${session.total_service_value.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Victim Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Victim Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Name</p>
                <p className="font-medium">
                  {session.victim_first_name || 'Unknown'} {session.victim_last_name || ''}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Gender</p>
                <p className="font-medium capitalize">{session.victim_gender || 'Unknown'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Is Minor</p>
                <p className="font-medium">
                  {session.is_minor === true ? 'Yes' : session.is_minor === false ? 'No' : 'Unknown'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Nationality</p>
                <p className="font-medium">{session.victim_nationality || 'Unknown'}</p>
              </div>
            </div>

            {/* Flags */}
            <div className="flex gap-2 pt-2">
              {session.needs_medical && (
                <span className="px-2 py-1 text-xs bg-destructive/10 text-destructive rounded">
                  Needs Medical
                </span>
              )}
              {session.needs_interpreter && (
                <span className="px-2 py-1 text-xs bg-chart-4/15 text-chart-4 rounded">
                  Needs Interpreter {session.interpreter_language && `(${session.interpreter_language})`}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Soft Room Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Soft Room
            </CardTitle>
          </CardHeader>
          <CardContent>
            {session.soft_room ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{session.soft_room.name}</span>
                </div>
                {session.soft_room.airport_code && (
                  <div className="text-sm text-muted-foreground">
                    Airport: {session.soft_room.airport_code}
                    {session.soft_room.terminal && `, Terminal ${session.soft_room.terminal}`}
                  </div>
                )}
                {session.soft_room.phone && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    {session.soft_room.phone}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground">No soft room assigned</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      {(session.intake_notes || session.session_notes) && (
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {session.intake_notes && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Intake Notes</p>
                <p className="text-foreground/80 whitespace-pre-wrap">{session.intake_notes}</p>
              </div>
            )}
            {session.session_notes && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Session Notes</p>
                <p className="text-foreground/80 whitespace-pre-wrap">{session.session_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Services Provided */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Services Provided ({session.services.length})
          </CardTitle>
          {session.status === 'in_progress' && (
            <Button size="sm" onClick={() => setShowAddService(!showAddService)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Service
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {/* Add Service Form */}
          {showAddService && (
            <form onSubmit={handleAddService} className="mb-4 space-y-4">
              {/* Service Type Selection Buttons */}
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase mb-2 block">Select Service Type</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                  {quickServiceButtons.map((svc) => (
                    <button
                      key={svc.type}
                      type="button"
                      onClick={() => setSelectedServiceType(selectedServiceType === svc.type ? '' : svc.type)}
                      className={cn(
                        'flex items-center gap-2 p-3 rounded-lg text-xs font-medium transition-all border',
                        selectedServiceType === svc.type
                          ? 'bg-primary/20 border-primary text-primary'
                          : 'bg-secondary border-border hover:bg-muted'
                      )}
                    >
                      <svc.icon size={14} />
                      {svc.label}
                    </button>
                  ))}
                </div>
              </div>

              {selectedServiceType && (
                <>
                  {/* Gift Card Details */}
                  {selectedServiceType === 'gift_card' && (
                    <div className="bg-secondary/50 border border-border rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <CreditCard size={16} className="text-primary" />
                        Gift Card Details
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Amount ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            placeholder="50"
                            value={serviceValue}
                            onChange={(e) => setServiceValue(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg bg-background border-border text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Card Type</label>
                          <select
                            value={giftCardType}
                            onChange={(e) => setGiftCardType(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg bg-background border-border text-sm"
                          >
                            <option value="Visa">Visa</option>
                            <option value="Mastercard">Mastercard</option>
                            <option value="Amazon">Amazon</option>
                            <option value="Target">Target</option>
                            <option value="Walmart">Walmart</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Food Voucher Details */}
                  {selectedServiceType === 'food_voucher' && (
                    <div className="bg-secondary/50 border border-border rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <DollarSign size={16} className="text-primary" />
                        Food Voucher Details
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Amount ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            placeholder="25"
                            value={serviceValue}
                            onChange={(e) => setServiceValue(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg bg-background border-border text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Vendor</label>
                          <select
                            value={foodVendor}
                            onChange={(e) => setFoodVendor(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg bg-background border-border text-sm"
                          >
                            <option value="Airport Restaurant">Airport Restaurant</option>
                            <option value="Food Court">Food Court</option>
                            <option value="Vending">Vending</option>
                            <option value="External Restaurant">External Restaurant</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Backpack Details */}
                  {selectedServiceType === 'backpack' && (
                    <div className="bg-secondary/50 border border-border rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <Package size={16} className="text-primary" />
                        Backpack Details
                      </div>
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Number of Backpacks</label>
                          <input
                            type="number"
                            min="1"
                            value={serviceQty}
                            onChange={(e) => setServiceQty(e.target.value)}
                            className="w-32 px-3 py-2 border rounded-lg bg-background border-border text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Contents Included</label>
                          <div className="flex flex-wrap gap-2">
                            {backpackContents.map((item) => (
                              <button
                                key={item}
                                type="button"
                                onClick={() => setBpContents(prev =>
                                  prev.includes(item) ? prev.filter(c => c !== item) : [...prev, item]
                                )}
                                className={cn(
                                  'px-2 py-1 rounded text-xs border transition-colors',
                                  bpContents.includes(item)
                                    ? 'bg-primary/20 border-primary text-primary'
                                    : 'bg-background border-border hover:bg-muted'
                                )}
                              >
                                {item}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Clothing Details */}
                  {selectedServiceType === 'clothing' && (
                    <div className="bg-secondary/50 border border-border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm font-semibold">
                          <Shirt size={16} className="text-primary" />
                          Clothing Items
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setClothingItems(prev => [...prev, { type: '', size: '', quantity: '1' }])}
                          className="text-xs h-7"
                        >
                          + Add Item
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {clothingItems.map((item, index) => (
                          <div key={index} className="flex items-center gap-2 bg-background p-2 rounded-lg border border-border">
                            <select
                              value={item.type}
                              onChange={(e) => {
                                const updated = [...clothingItems]
                                updated[index] = { ...updated[index], type: e.target.value }
                                setClothingItems(updated)
                              }}
                              className="flex-1 h-9 px-2 rounded border border-border bg-background text-sm"
                            >
                              <option value="">Select type...</option>
                              {clothingTypes.map((type) => (
                                <option key={type} value={type}>{type}</option>
                              ))}
                            </select>
                            <select
                              value={item.size}
                              onChange={(e) => {
                                const updated = [...clothingItems]
                                updated[index] = { ...updated[index], size: e.target.value }
                                setClothingItems(updated)
                              }}
                              className="w-20 h-9 px-2 rounded border border-border bg-background text-sm"
                            >
                              <option value="">Size</option>
                              {clothingSizes.map((size) => (
                                <option key={size} value={size}>{size}</option>
                              ))}
                            </select>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => {
                                const updated = [...clothingItems]
                                updated[index] = { ...updated[index], quantity: e.target.value }
                                setClothingItems(updated)
                              }}
                              className="w-16 h-9 text-center px-2 rounded border border-border bg-background text-sm"
                              placeholder="Qty"
                            />
                            {clothingItems.length > 1 && (
                              <button
                                type="button"
                                onClick={() => setClothingItems(prev => prev.filter((_, i) => i !== index))}
                                className="text-destructive hover:text-destructive/80 p-1"
                              >
                                x
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Common fields: Value, Quantity, Notes (for non-specialized types) */}
                  {!['gift_card', 'food_voucher', 'backpack', 'clothing'].includes(selectedServiceType) && (
                    <div className="bg-secondary/50 border border-border rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Value ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            placeholder="0"
                            value={serviceValue}
                            onChange={(e) => setServiceValue(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg bg-background border-border text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Quantity</label>
                          <input
                            type="number"
                            min="1"
                            value={serviceQty}
                            onChange={(e) => setServiceQty(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg bg-background border-border text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Notes for all types */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-muted-foreground uppercase">Notes</label>
                    <input
                      type="text"
                      value={serviceNotes}
                      onChange={(e) => setServiceNotes(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                      placeholder="Optional notes..."
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button type="submit" size="sm" disabled={addServiceMutation.isPending} className="gap-1.5">
                      <Check className="h-3.5 w-3.5" />
                      {addServiceMutation.isPending ? 'Adding...' : `Add ${serviceTypeLabels[selectedServiceType] || 'Service'}`}
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => { resetServiceForm(); setShowAddService(false) }}>
                      Cancel
                    </Button>
                  </div>
                </>
              )}

              {!selectedServiceType && (
                <div className="flex justify-end">
                  <Button type="button" size="sm" variant="outline" onClick={() => setShowAddService(false)}>
                    Cancel
                  </Button>
                </div>
              )}
            </form>
          )}

          {/* Services List */}
          {session.services.length > 0 ? (
            <div className="space-y-2">
              {session.services.map((service) => (
                <div
                  key={service.id}
                  className="flex items-center justify-between p-3 bg-secondary rounded-lg"
                >
                  <div>
                    <p className="font-medium">
                      {serviceTypeLabels[service.service_type] || service.service_type}
                    </p>
                    {service.notes && (
                      <p className="text-sm text-muted-foreground">{service.notes}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {service.provided_at && formatDateTime(service.provided_at)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-chart-3">
                      ${service.monetary_value.toFixed(2)}
                    </p>
                    {service.quantity > 1 && (
                      <p className="text-xs text-muted-foreground">x{service.quantity}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">No services recorded yet</p>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      {session.status === 'in_progress' && (
        <Card>
          <CardContent className="p-4">
            <Button
              onClick={() => completeMutation.mutate(undefined)}
              disabled={completeMutation.isPending}
            >
              <Check className="h-4 w-4 mr-2" />
              {completeMutation.isPending ? 'Completing...' : 'Complete Session'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Responder Details */}
      <Card>
        <CardHeader>
          <CardTitle>Responder Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">User ID</p>
              <p className="font-mono text-sm">{session.responder_user_id || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Name</p>
              <p className="font-medium">{session.responder_name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Agency</p>
              <p className="font-medium">{session.responder_agency || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Badge ID</p>
              <p className="font-medium">{session.responder_badge_id || 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
